import { Group, Menu } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	apiQueryKey,
	useCancelAccessRequest,
	useDeleteAccessRequest,
	useGetAccessRequest,
	useRejectAccessRequest,
} from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import type { ButtonProps } from '@repo/foundations';
import { Button, IconButton } from '@repo/foundations';
import { useCallback } from 'react';
import { queryClient, useAuthUser } from '../../../api';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import {
	closeAllModals,
	openDeleteConfirmModal,
	openModal,
} from '../../ModalManager/events';
import { DataAccessRequestApproveModal } from '../DataAccessRequestApproveModal/DataAccessRequestApproveModal';
import { DataAccessRequestCreateModal } from '../DataAccessRequestCreateModal/DataAccessRequestCreateModal';
import { DataAccessRequestControlsSkeleton } from './DataAccessRequestControlsSkeleton';

export interface DataAccessRequestControlsProps {
	requestId: string;
}

export function DataAccessRequestControls({
	requestId,
}: DataAccessRequestControlsProps) {
	const { user: currentUser } = useAuthUser();
	const { data: dataAccessRequest, isLoading } = useGetAccessRequest({
		pathParams: {
			requestId,
		},
	});

	const { mutate: rejectAccessRequest } = useRejectAccessRequest({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('integration_v2/data-access-requests'),
			});
			showNotification({
				title: 'Access request rejected',
				message: 'The request has been rejected',
				color: 'red',
			});
		},
	});

	const { mutate: cancelAccessRequest } = useCancelAccessRequest({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('integration_v2/data-access-requests'),
			});
			showNotification({
				title: 'Access request revoked',
				message: 'The request has been revoked',
				color: 'blue',
			});
		},
	});

	const { mutate: deleteAccessRequest } = useDeleteAccessRequest({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('integration_v2/data-access-requests'),
			});
			queryClient.invalidateQueries({
				queryKey: apiQueryKey(
					'integration_v2/data-access-requests/{request_id}',
					{
						request_id: requestId,
					}
				),
			});
			showNotification({
				title: 'Access request deleted',
				message: 'The request has been deleted',
				color: 'green',
			});
		},
	});

	const handleDenyAccess = useCallback(() => {
		rejectAccessRequest({
			pathParams: {
				requestId,
			},
			body: {},
		});
	}, [rejectAccessRequest, requestId]);

	const handleApproveAccess = useCallback(() => {
		if (!dataAccessRequest) {
			return;
		}

		openModal({
			title: 'Approve access request',
			size: 480,
			centered: true,
			children: (
				<DataAccessRequestApproveModal
					request={dataAccessRequest}
					onClose={closeAllModals}
				/>
			),
		});
	}, [dataAccessRequest]);

	const handleDeleteRequest = useCallback(() => {
		openDeleteConfirmModal({
			title: 'Delete request?',
			description: "You can't undo this action.",
			confirmLabel: 'Delete request',
			confirmProps: {
				variant: 'primary',
				tone: 'critical',
			},
			onConfirm: () => {
				deleteAccessRequest({
					pathParams: {
						requestId,
					},
				});
			},
		});
	}, [deleteAccessRequest, requestId]);

	const handleCancelAccess = useCallback(() => {
		cancelAccessRequest({
			pathParams: {
				requestId,
			},
			body: {},
		});
	}, [cancelAccessRequest, requestId]);

	const handleEditRequest = useCallback(() => {
		openModal({
			title: 'Edit access request',
			size: 480,
			children: (
				<DataAccessRequestCreateModal
					request={{
						id: dataAccessRequest?.id,
						integration_id: dataAccessRequest?.integration_id ?? undefined,
						requested_resources: dataAccessRequest?.requested_resources,
						requested_expires_at: dataAccessRequest?.requested_expires_at,
						requested_text: dataAccessRequest?.requested_text,
					}}
					onClose={closeAllModals}
				/>
			),
		});
	}, [dataAccessRequest]);

	const { hasPermission: isAccessRequestsAdmin } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Workspace.Manage',
	});

	const { hasPermission: canUpdateAccessRequests } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'DataAccessRequests.Update',
	});

	const { hasPermission: canDeleteAccessRequests } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'DataAccessRequests.Delete',
	});

	const canShowApproveDenyRequest =
		isAccessRequestsAdmin || canUpdateAccessRequests;

	const canShowDeleteRequest =
		canDeleteAccessRequests ||
		currentUser?.id === dataAccessRequest?.created_by?.id;

	if (isLoading || !dataAccessRequest) {
		return <DataAccessRequestControlsSkeleton />;
	}

	// Hide all action buttons if request has ServiceNow issue attached
	if (dataAccessRequest.servicenow_issue) {
		return null;
	}

	const controls: Array<{
		label: string;
		show: boolean;
		canCollapse: boolean;
		onClick: () => void;
		variant: ButtonProps['variant'];
	}> = [
		{
			label: 'Edit',
			show:
				currentUser?.id === dataAccessRequest?.created_by?.id &&
				dataAccessRequest?.status === 'pending',
			canCollapse: true,
			onClick: handleEditRequest,
			variant: 'default',
		},
		{
			label: 'Delete',
			show: canShowDeleteRequest && dataAccessRequest?.status === 'pending',
			canCollapse: true,
			onClick: handleDeleteRequest,
			variant: 'default',
		},
		{
			label: 'Deny access',
			show:
				canShowApproveDenyRequest && dataAccessRequest?.status === 'pending',
			canCollapse: false,
			onClick: handleDenyAccess,
			variant: 'default',
		},
		{
			label: 'Approve access',
			show:
				canShowApproveDenyRequest && dataAccessRequest?.status === 'pending',
			canCollapse: false,
			onClick: handleApproveAccess,
			variant: 'primary',
		},
		{
			label: 'Revoke access',
			show: dataAccessRequest?.status === 'approved',
			canCollapse: false,
			onClick: handleCancelAccess,
			variant: 'primary',
		},
	];

	const controlsToShow = controls.filter((control) => control.show);
	const shouldCollapse =
		controlsToShow.length > 2 &&
		controlsToShow.filter((control) => control.canCollapse).length > 1;

	return (
		<Group spacing="xs">
			{shouldCollapse && (
				<Menu>
					<Menu.Target>
						<IconButton iconName="dots" variant="tertiary" />
					</Menu.Target>
					<Menu.Dropdown>
						{controlsToShow
							.filter((control) => control.canCollapse)
							.map((control) => (
								<Menu.Item key={control.label} onClick={control.onClick}>
									{control.label}
								</Menu.Item>
							))}
					</Menu.Dropdown>
				</Menu>
			)}
			{controlsToShow
				.filter((control) => !shouldCollapse || !control.canCollapse)
				.map((control) => (
					<Button
						key={control.label}
						variant={control.variant}
						onClick={control.onClick}
						size="md"
					>
						{control.label}
					</Button>
				))}
		</Group>
	);
}
